import React, { useState, useEffect } from "react";
import { adminCategoryService } from "../../services/api";
import { Category } from "../../types";
import LoadingSpinner from "../../components/LoadingSpinner";
import Modal from "../../components/Modal";

const Categories: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(
        null,
    );
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(
        null,
    );

    // Form states
    const [formName, setFormName] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setIsLoading(true);
            const data = await adminCategoryService.getAll();
            setCategories(data);
        } catch (err) {
            setError("Erreur lors du chargement des categories");
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingCategory(null);
        setFormName("");
        setFormDescription("");
        setIsModalOpen(true);
    };

    const openEditModal = (category: Category) => {
        setEditingCategory(category);
        setFormName(category.name);
        setFormDescription(category.description || "");
        setIsModalOpen(true);
    };

    const openDeleteModal = (category: Category) => {
        setCategoryToDelete(category);
        setIsDeleteModalOpen(true);
    };

    const moveCategory = async (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= categories.length) return;

        const reordered = [...categories];
        [reordered[index], reordered[targetIndex]] = [
            reordered[targetIndex],
            reordered[index],
        ];
        setCategories(reordered);

        try {
            await adminCategoryService.reorder(reordered.map((c) => c.id));
        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                    "Erreur lors du changement d'ordre",
            );
            loadCategories();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setIsSaving(true);

        try {
            if (editingCategory) {
                await adminCategoryService.update(editingCategory.id, {
                    name: formName,
                    description: formDescription || undefined,
                    image: "/log.png",
                });
                setSuccess("Categorie mise a jour avec succes");
            } else {
                await adminCategoryService.create({
                    name: formName,
                    description: formDescription || undefined,
                    image: "/log.png",
                });
                setSuccess("Categorie creee avec succes");
            }
            setIsModalOpen(false);
            loadCategories();
        } catch (err: any) {
            setError(
                err.response?.data?.error || "Erreur lors de la sauvegarde",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!categoryToDelete) return;

        setIsSaving(true);
        try {
            await adminCategoryService.delete(categoryToDelete.id);
            setSuccess("Categorie supprimee avec succes");
            setIsDeleteModalOpen(false);
            setCategoryToDelete(null);
            loadCategories();
        } catch (err: any) {
            setError(
                err.response?.data?.error || "Erreur lors de la suppression",
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Clear messages after 3 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Chargement des categories..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">
                            Gestion des Categories
                        </h1>
                        <p className="text-gray-400">
                            {categories.length} categorie(s) au total
                        </p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="btn-primary mt-4 sm:mt-0"
                    >
                        <span className="flex items-center space-x-2">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                            </svg>
                            <span>Nouvelle categorie</span>
                        </span>
                    </button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">
                        {success}
                    </div>
                )}

                {/* Categories List */}
                {categories.length === 0 ? (
                    <div className="card p-12 text-center">
                        <svg
                            className="w-16 h-16 mx-auto text-gray-600 mb-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                        </svg>
                        <p className="text-gray-400 text-lg mb-4">
                            Aucune categorie pour le moment
                        </p>
                        <button
                            onClick={openCreateModal}
                            className="btn-primary"
                        >
                            Creer une categorie
                        </button>
                    </div>
                ) : (
                    <div className="card overflow-hidden">
                        <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px]">
                            <thead className="bg-primary-dark/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">
                                        Ordre
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">
                                        Image
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">
                                        Nom
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 hidden sm:table-cell">
                                        Description
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-300 hidden md:table-cell">
                                        Date de creation
                                    </th>
                                    <th className="px-6 py-4 text-right text-sm font-medium text-gray-300">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/10">
                                {categories.map((category, index) => (
                                    <tr
                                        key={category.id}
                                        className="hover:bg-primary-dark/30 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center space-x-1">
                                                <button
                                                    onClick={() =>
                                                        moveCategory(
                                                            index,
                                                            -1,
                                                        )
                                                    }
                                                    disabled={index === 0}
                                                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                                    title="Monter"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M5 15l7-7 7 7"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        moveCategory(
                                                            index,
                                                            1,
                                                        )
                                                    }
                                                    disabled={
                                                        index ===
                                                        categories.length - 1
                                                    }
                                                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                                                    title="Descendre"
                                                >
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <img
                                                src="/log.png"
                                                alt={category.name}
                                                className="w-10 h-10 rounded-lg object-cover border border-white/10"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-white font-medium">
                                                {category.name}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 hidden sm:table-cell">
                                            <span className="text-gray-400 text-sm line-clamp-2">
                                                {category.description || "-"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell">
                                            <span className="text-gray-400 text-sm">
                                                {new Date(
                                                    category.created_at,
                                                ).toLocaleDateString("fr-FR")}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end space-x-2">
                                                <button
                                                    onClick={() =>
                                                        openEditModal(category)
                                                    }
                                                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors"
                                                    title="Modifier"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        openDeleteModal(
                                                            category,
                                                        )
                                                    }
                                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                                    title="Supprimer"
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}

                {/* Create/Edit Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={
                        editingCategory
                            ? "Modifier la categorie"
                            : "Nouvelle categorie"
                    }
                >
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-300 mb-2"
                            >
                                Nom *
                            </label>
                            <input
                                id="name"
                                type="text"
                                value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                className="input-field"
                                placeholder="Nom de la categorie"
                                required
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="description"
                                className="block text-sm font-medium text-gray-300 mb-2"
                            >
                                Description
                            </label>
                            <textarea
                                id="description"
                                value={formDescription}
                                onChange={(e) =>
                                    setFormDescription(e.target.value)
                                }
                                className="input-field min-h-[100px] resize-none"
                                placeholder="Description (optionnelle)"
                            />
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="btn-primary disabled:opacity-50"
                            >
                                {isSaving
                                    ? "Sauvegarde..."
                                    : editingCategory
                                      ? "Mettre a jour"
                                      : "Creer"}
                            </button>
                        </div>
                    </form>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title="Confirmer la suppression"
                    size="sm"
                >
                    <div className="space-y-4">
                        <p className="text-gray-300">
                            Etes-vous sur de vouloir supprimer la categorie{" "}
                            <span className="text-white font-semibold">
                                {categoryToDelete?.name}
                            </span>{" "}
                            ?
                        </p>
                        <p className="text-red-400 text-sm">
                            Cette action est irreversible et supprimera tous les
                            produits associes.
                        </p>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="btn-secondary"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isSaving}
                                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {isSaving ? "Suppression..." : "Supprimer"}
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Categories;
